import { useState, useRef, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import axiosService from "../../../core/services/axios-service";
import { ApiRoutes } from "../../../core/dictionary/api-url";
import { MetricsCard } from "../../../components/designs/matrics-card";
import { BarChart3, Building2, UserPlus, Users } from "lucide-react";
import { ListingHeader } from "../../../components/listing-header";
import Loader from "../../../components/designs/loader";

export default function DashboardPage() {
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [error, setError] = useState("");
  const [graphOpen, setGraphOpen] = useState(true);
  const contentRef = useRef(null);
  const [height, setHeight] = useState("auto");

  useEffect(() => {
    setDashboardLoading(true);
    axiosService
      .get(ApiRoutes.DASHBOARD_METRICS) // Replace with your actual endpoint
      .then((res) => {
        if (res.data && res.data.status === "success") {
          setDashboardData(res.data.data);
        } else {
          setError("Failed to fetch dashboard data.");
        }
      })
      .catch(() => setError("Server error. Please try again."))
      .finally(() => setDashboardLoading(false));
  }, []);
  useEffect(() => {
    if (graphOpen && contentRef.current) {
      setHeight(contentRef.current.scrollHeight + "px");
    } else {
      setHeight("0px");
    }
  }, [graphOpen, dashboardData]);
  // Highcharts options for Organizations vs Leads (Line Chart)
  const comparisonChartOptions = dashboardData
    ? {
        chart: { type: "spline", height: 340, spacing: [16, 16, 16, 16] },
        title: { text: "" },
        xAxis: { categories: dashboardData.monthly.labels },
        yAxis: { min: 0, title: { text: "Count" } },
        series: [
          {
            name: "Organizations",
            data: dashboardData.monthly.organizationsPerMonth,
            color: "#003f86",
            lineWidth: 3,
            marker: { symbol: "circle", radius: 5 },
          },
          {
            name: "Leads",
            data: dashboardData.monthly.leadsPerMonth,
            color: "#1db954",
            lineWidth: 3,
            marker: { symbol: "diamond", radius: 5 },
          },
        ],
        credits: { enabled: false },
        legend: { align: "center", verticalAlign: "top", layout: "horizontal" },
        tooltip: { shared: true },
      }
    : {};
  if (dashboardLoading)
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8f9fa",
        }}
      >
        <Loader text={"Loading Dashboard Details..."} />
      </div>
    );
  if (error) return <div className="text-danger">{error}</div>;
  if (!dashboardData) return null;
  return (
    <>
      <ListingHeader
        title={"Dashboard"}
        breadCrumb={[{ label: "Dashboard", href: "/admin/dashboard" }]}
        actions={[]}
      />
      <div className="flex-grow-1 p-4">
        <div className="container-fluid p-0">
          <div className="row g-4 mb-4">
            <div className="col-12">
              <div
                className="card shadow-sm"
                style={{
                  borderRadius: "16px",
                  padding: "0",
                  background: "#fff",
                  boxShadow: "0 2px 12px rgba(0,40,85,0.06)",
                }}
              >
                <div
                  className="d-flex align-items-center justify-content-between"
                  style={{
                    borderBottom: "1px solid #f0f2f5",
                    padding: "24px",
                    cursor: "pointer",
                    borderRadius: "16px 16px 0 0",
                  }}
                  onClick={() => setGraphOpen((prev) => !prev)}
                >
                  <h4
                    style={{
                      color: "#003f86",
                      fontWeight: 700,
                      margin: 0,
                      fontSize: "1.25rem",
                      letterSpacing: "0.01em",
                    }}
                  >
                    Organizations vs Leads Per Month
                  </h4>
                  <span
                    style={{
                      fontSize: "2rem",
                      color: "#003f86",
                      borderRadius: "50px",
                      transition: "background 0.2s",
                      padding: "2px 12px",
                      cursor: "pointer",
                      background: "transparent",
                    }}
                  >
                    {graphOpen ? "−" : "+"}
                  </span>
                </div>
                <div
                  ref={contentRef}
                  style={{
                    overflow: "hidden",
                    transition: "height 0.4s cubic-bezier(.4,0,.2,1)",
                    height: height,
                    padding: graphOpen ? "32px 24px" : "0 24px",
                    background: "#fff",
                  }}
                >
                  {dashboardData && (
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={comparisonChartOptions}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="row g-4 mb-4">
            <div className="col-xl-6 col-lg-6">
              <MetricsCard
                title="Total Organizations"
                value={
                  dashboardLoading
                    ? "--"
                    : dashboardData?.totalOrganizations ?? "--"
                }
                icon={Building2}
              />
            </div>
            <div className="col-xl-6 col-lg-6">
              <MetricsCard
                title="Organizations This Month"
                value={
                  dashboardLoading
                    ? "--"
                    : dashboardData?.organizationsInRange ?? "--"
                }
                icon={Users}
              />
            </div>
            <div className="col-xl-6 col-lg-6">
              <MetricsCard
                title="Total Coaches"
                value={
                  dashboardLoading ? "--" : dashboardData?.totalCoaches ?? "--"
                }
                icon={UserPlus}
              />
            </div>
            <div className="col-xl-6 col-lg-6">
              <MetricsCard
                title="Total Team Members"
                value={
                  dashboardLoading
                    ? "--"
                    : dashboardData?.totalTeamMembers ?? "--"
                }
                icon={Users}
              />
            </div>
            <div className="col-xl-6 col-lg-6">
              <MetricsCard
                title="Total Leads"
                value={
                  dashboardLoading ? "--" : dashboardData?.totalLeads ?? "--"
                }
                icon={BarChart3}
              />
            </div>
            <div className="col-xl-6 col-lg-6">
              <MetricsCard
                title="Leads This Month"
                value={
                  dashboardLoading ? "--" : dashboardData?.leadsInRange ?? "--"
                }
                icon={BarChart3}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
